import { ThemeProvider } from '@cerebral-inc/ui';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { BasicSpinner } from 'components/shared/spinner';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { ApiError } from 'types/error';

// You can add other apis here
export interface Api {
  serverApi: AxiosInstance;
}

const defaultValue: Api = {
  serverApi: axios
};

const ApiContext = createContext<Api>(defaultValue);

export interface ApiProviderProps {
  accessToken: string;
  client: string;
  uid: string;
  jwtToken: string;
  serverbaseUrl: string;
  children: ReactNode;
}

const getHeaders = ({
  accessToken,
  client,
  uid,
  jwtToken
}: Omit<ApiProviderProps, 'children' | 'serverbaseUrl'>) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'access-token': accessToken,
    client,
    uid
  };

  if (jwtToken) {
    headers.Authorization = `Bearer ${jwtToken}`;
  }

  return headers;
};

const successHandler = (response: AxiosResponse) => response;

const errorHandler = (
  error: AxiosError<{ message: string; error: string }>
): ApiError => {
  throw new ApiError(
    error.response?.data?.message ?? 'Internal server error',
    error.response?.status ?? 500,
    error.response?.data
  );
};

export const ApiProvider = ({
  accessToken,
  client,
  uid,
  jwtToken,
  serverbaseUrl,
  children
}: ApiProviderProps) => {
  const [value, setValue] = useState<Api>(defaultValue);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const headers = getHeaders({ accessToken, client, jwtToken, uid });
    const serverApi = axios.create({ headers, baseURL: serverbaseUrl });

    serverApi.interceptors.response.use(successHandler, errorHandler);

    setValue({ serverApi });
    setIsLoading(false);
  }, [accessToken, client, uid, jwtToken, serverbaseUrl]);

  return (
    <ApiContext.Provider value={value}>
      {isLoading ? (
        <ThemeProvider>
          <BasicSpinner />
        </ThemeProvider>
      ) : (
        children
      )}
    </ApiContext.Provider>
  );
};

export const useApi = () => useContext(ApiContext);
